import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from "../firebase";
import { 
  collection, 
  getDocs, 
  addDoc, 
  doc, 
  runTransaction, 
  Timestamp 
} from 'firebase/firestore';
import './AddGrnPage.css'; // This will be our new, corrected CSS file
import { FaPlus, FaTrash } from 'react-icons/fa';

function AddGrnPage() {
  const navigate = useNavigate();

  // State for all form fields and data
  const [products, setProducts] = useState([]);
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [receivedBy, setReceivedBy] = useState('');
  const [invoiceBase64, setInvoiceBase64] = useState('');
  const [grnItems, setGrnItems] = useState([]);
  
  const [currentItem, setCurrentItem] = useState({
    productId: '',
    productName: '',
    unit: '',
    availableUnits: [],
    quantityReceived: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch products from Firestore on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsSnapshot = await getDocs(collection(db, 'groceryProducts'));
        const productsList = productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProducts(productsList);
      } catch (err) {
        setError("Failed to load products.");
      }
    };
    fetchProducts();
  }, []);

  // Handler for when a product is selected
  const handleProductSelect = (e) => {
    const productId = e.target.value;
    const product = products.find(p => p.id === productId);
    if (product) {
      setCurrentItem({
        productId: product.id,
        productName: product.name,
        unit: '',
        availableUnits: product.variants?.map(v => v.unit) || [],
        quantityReceived: '',
      });
    }
  };

  // Generic handler for input changes
  const handleItemInputChange = (e) => {
    setCurrentItem({ ...currentItem, [e.target.name]: e.target.value });
  };
  
  // Adds the current item to the GRN list
  const handleAddItem = () => {
    const { productId, productName, unit, quantityReceived } = currentItem;
    if (!productId || !unit || !quantityReceived) {
      setError("Please select a product, unit, and quantity.");
      return;
    }
    const newItem = { productId, productName, unit, quantityReceived: parseInt(quantityReceived, 10), purchasePrice: 0 };
    setGrnItems([...grnItems, newItem]);
    setCurrentItem({ productId: '', productName: '', unit: '', availableUnits: [], quantityReceived: '' });
    setError('');
  };

  // Removes an item from the GRN list
  const handleRemoveItem = (index) => setGrnItems(grnItems.filter((_, i) => i !== index));
  
  // Handles invoice file upload
  const handleInvoiceChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => setInvoiceBase64(reader.result);
    reader.onerror = () => setError("Failed to read invoice file.");
  };

  // Main submission handler to save GRN and update stock
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!purchaseDate || !receivedBy) return setError("Please fill out Purchase Date and Received By.");
    if (grnItems.length === 0) return setError("Please add at least one item.");
    
    setLoading(true);
    setError('');
    try {
      const grnData = {
        totalPurchasePrice: 0,
        purchaseDate: Timestamp.fromDate(new Date(purchaseDate)),
        receivedBy: receivedBy,
        invoiceUrl: invoiceBase64 || null,
        createdAt: Timestamp.now(),
        items: grnItems
      };
      await addDoc(collection(db, 'grocery grns'), grnData);
      await runTransaction(db, async (transaction) => {
        for (const item of grnItems) {
          const productRef = doc(db, 'groceryProducts', item.productId);
          const productDoc = await transaction.get(productRef);
          if (!productDoc.exists()) throw new Error(`Product "${item.productName}" not found.`);
          const productData = productDoc.data();
          const newVariants = productData.variants.map(variant => 
            variant.unit === item.unit 
              ? { ...variant, quantity: (variant.quantity || 0) + item.quantityReceived } 
              : variant
          );
          transaction.update(productRef, { variants: newVariants });
        }
      });
      alert("GRN created and stock updated successfully!");
      navigate('/grns');
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-grn-page">
      <h2 className="page-title">Add New Goods Received Note (GRN)</h2>
      <form onSubmit={handleSubmit} className="grn-form">
        {error && <p className="error-message">{error}</p>}
        
        <div className="form-section">
            <div className="form-group">
                <label htmlFor="receivedBy">Received By</label>
                <input type="text" id="receivedBy" value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} placeholder="Your Name" required />
            </div>
            <div className="form-group">
                <label htmlFor="purchaseDate">Purchase On Date</label>
                <input type="date" id="purchaseDate" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} required />
            </div>
            <div className="form-group">
                <label htmlFor="invoice">Upload Bill/Invoice (Optional)</label>
                <input type="file" id="invoice" onChange={handleInvoiceChange} accept="image/*,application/pdf" />
            </div>
        </div>
        
        <h3 className="section-title">Add Items to GRN</h3>
        
        <div className="add-item-container">
            <div className="form-group">
                <label>Product</label>
                <select value={currentItem.productId} onChange={handleProductSelect} required>
                    <option value="" disabled>Select a product</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
            </div>
            <div className="form-group">
                <label>Unit / Variant</label>
                <select name="unit" value={currentItem.unit} onChange={handleItemInputChange} disabled={!currentItem.productId} required>
                    <option value="" disabled>Select a unit</option>
                    {currentItem.availableUnits.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                </select>
            </div>
            <div className="form-group">
                <label>Quantity</label>
                <input type="number" name="quantityReceived" value={currentItem.quantityReceived} onChange={handleItemInputChange} placeholder="e.g., 50" required />
            </div>
            <button type="button" className="add-item-button" onClick={handleAddItem}>
                <FaPlus /> Add Item
            </button>
        </div>

        {grnItems.length > 0 && (
          <div className="table-wrapper">
            <table className="items-table">
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Unit</th>
                  <th>Quantity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {grnItems.map((item, index) => (
                  <tr key={index}>
                    <td>{item.productName}</td>
                    <td>{item.unit}</td>
                    <td>{item.quantityReceived}</td>
                    <td>
                      <button type="button" className="action-button delete-button" onClick={() => handleRemoveItem(index)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? 'Saving...' : 'Create GRN'}
        </button>
      </form>
    </div>
  );
}

export default AddGrnPage;